import React, { useEffect, useRef, useState } from "react";
import { useSession } from "../../../util/Session";
import { Alert, Button, Divider, Form, Input, Space, message as msg } from "antd";
import { Link, useHistory } from "react-router-dom";
import { apiCall } from "../../utilities/Api";
import { ExclamationCircleFilled, MailOutlined, MessageOutlined } from "@ant-design/icons";
import Cookies from "js-cookie";
import { openWindow } from "../../../util/Utils";
import CodeInput from "../CodeInput";

function AccountLoginForm(props) {
  const {
    show_create = true,
    email = "",
    show_forgot = true,
    recover_email = false,
    recover_text = false,
    size = "middle",
    redirect = false,
    callback = () => { },
    dismiss,
    header = <></>,
    footer = <></>,
    focus_password = false,
    login_primary = true,
    init_view = "login",
    back = () => { },
    back_label = "Back to login",
    autofire_verification = false,
  } = props;

  const history = useHistory();
  const [session, setSession] = useSession();

  const [loginForm] = Form.useForm();
  const [view, setView] = useState(init_view);
  const [recoveryMethod, setRecoveryMethod] = useState("");
  const [recoveryDestination, setRecoveryDestination] = useState("");
  const [codeResetKey, setCodeResetKey] = useState(0);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [loading, setLoading] = useState(false);

  const hasRecovery = recover_email || recover_text;
  const loginInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  const watchedEmail = Form.useWatch("username", loginForm);

  useEffect(() => {
    if (view !== "login") {
      return;
    }

    requestAnimationFrame(() => {

      if (focus_password) {
        if (passwordInputRef.current) {
          passwordInputRef.current.focus();
        }
      } else {
        if (loginInputRef.current) {
          loginInputRef.current.focus();
        }
      }

    });
  }, [view]);

  const clearMessage = () => {
    setMessage("");
    setMessageType("");
  };

  const showError = (text) => {
    setMessage(text);
    setMessageType("error");
  };

  const getEmailAddress = () => {
    return (loginForm.getFieldValue("username")?.trim() || email.trim() || "");
  };

  const checkTransfer = (_result) => {
    if (_result?.STG) {
      Cookies.set("STG", _result.STG, { expires: 7 });
      Cookies.set("want_v1", 1, { expires: 7 });
      window.location.reload();
      return true;
    }
    return false;
  };

  const completeLogin = (_result) => {
    if (checkTransfer(_result)) { return; }

    setSession(_result);
    loginForm.resetFields();
    callback();

    if (dismiss) { dismiss(); }
    if (window.loginRedirect) {
      const redirectPath = window.loginRedirect;
      window.loginRedirect = false;
      history.push(redirectPath);
      return;
    }

    if (redirect) {
      history.push("/");
    }
  };

  const login = (_formValues) => {
    clearMessage();
    setLoading(true);

    let loginKey = "san";

    if (_formValues.username.includes("@")) {
      loginKey = "email_address";
    }

    apiCall("account/login", { [loginKey]: _formValues.username, password: _formValues.password, }, (_s, _r) => {
      setLoading(false);

      if (!_s) {
        loginForm.setFieldsValue({ password: "", });
        showError(_r?.error || "Invalid login.");
        return;
      }

      if (_s, _r?.url) {
        openWindow(_r.url, "blank");
        if (dismiss) { dismiss(); }
        return;
      }

      completeLogin(_r);
    });
  };

  const validateRecoveryEmail = () => {
    const emailAddress = getEmailAddress();

    if (!emailAddress) {
      loginForm.setFields([{ name: "username", errors: ["Please enter your email address."], },]);
      return false;
    }

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress);

    if (!isValidEmail) {
      loginForm.setFields([{ name: "username", errors: ["Please enter a valid email address."], },]);
      return false;
    }

    loginForm.setFields([{ name: "username", errors: [], },]);

    return true;
  };


  const [resendSeconds, setResendSeconds] = useState(0);
  useEffect(() => {
    if (resendSeconds <= 0) {
      return;
    }

    const timer = setTimeout(() => {
      setResendSeconds(current => current - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [resendSeconds]);

  const sendLoginCode = (_method, delay = false) => {
    if (loading) {
      return;
    }
    if (!props?.recover_email && !validateRecoveryEmail()) {
      return;
    }
    clearMessage();
    setLoading(true);
    setRecoveryMethod(_method);

    const emailAddress = (props?.recover_email && typeof props?.recover_email === "string") ? props.recover_email : getEmailAddress();
    const endpoint = _method === "text" ? "account/sendVerificationCodeByPhone" : "account/sendVerificationCodeByEmail";

    apiCall(endpoint, { email: emailAddress, check_account: false }, (_status, _result) => {

      setLoading(false);
      if (!_status) {
        setRecoveryMethod("");
        showError(
          _result?.error || "Unable to send the login code."
        );

        return;
      }

       setResendSeconds(30);
      const destination = _result?.destination || _result?.phone || emailAddress;
      setRecoveryDestination(destination);
      setCodeResetKey((current) => current + 1);
      setView("code");
    }
    );
  };


  useEffect(() => {

    if (autofire_verification) {
      sendLoginCode("email");
    }

  }, [])

  const verifyingRef = useRef(false);

  const verifyRecoveryCode = (_code) => {
    if (verifyingRef.current || _code.length !== 6) {
      return;
    }

    verifyingRef.current = true;

    clearMessage();
    setLoading(true);

    const emailAddress = (props?.recover_email && typeof props?.recover_email === "string") ? props.recover_email : getEmailAddress();

    const endpoint = "account/verifyEmailWithCode"

    apiCall(endpoint, { email: emailAddress, code: _code, }, (_status, _result) => {
      setLoading(false);
      verifyingRef.current = false;
      if (!_status) {
        showError(_result?.error || "The login code is incorrect or has expired.");
        setCodeResetKey((current) => current + 1);
        return;
      }

      if (_result?.success) {
        callback("new_user");
      } else {
        completeLogin(_result);
      }

    }
    );
  };


  const returnToLogin = () => {
    clearMessage();
    setLoading(false);
    setRecoveryMethod("");
    setRecoveryDestination("");
    setCodeResetKey((current) => current + 1);
    setView("login");
  };

  const drawMessage = () => {
    if (!message) {
      return null;
    }

    return (
      <Alert
        showIcon
        type={messageType || "info"}
        message={message}
        style={{
          marginBottom: 15,
        }}
      />
    );
  };

  const drawLogin = () => {

    const emailAddress = (props?.recover_email && typeof props?.recover_email === "string") ? props.recover_email : getEmailAddress();
    return (
      <>
        {header}

        <Form
          autoComplete="off"
          name="account-login"
          form={loginForm}
          layout="vertical"
          onFinish={login}
          initialValues={{
            username: email,
            remember: true,
          }}
        >
          <Form.Item
            name="username"
            rules={[
              {
                required: true,
                message: "Please enter your email address.",
              },
            ]}
          >
            <Input
              aria-label="Email address"
              id="email"
              ref={loginInputRef}
              autoFocus
              inputMode="email"
              autoComplete="email"
              size={size}
              placeholder="Email address"
              disabled={(loading || focus_password)}
              onChange={clearMessage}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              {
                required: true,
                message: "Password required.",
              },
            ]}
          >
            <Input.Password
              aria-label="Password"
              id="password"
              ref={passwordInputRef}
              autoComplete="current-password"
              size={size}
              placeholder="Password"
              disabled={loading}
              onChange={clearMessage}
            />
          </Form.Item>

          {messageType === "error" && message && (
            <p
              aria-live="assertive"
              className="error"
              style={{
                paddingBottom: 15,
                color: "#ff4d4f",
                textAlign: "center",
              }}
            >
              <ExclamationCircleFilled /> {message}
            </p>
          )}

          <Button
            block
            type={(login_primary) ? "primary" : "default"}
            htmlType="submit"
            size={size}
            loading={loading && !recoveryMethod}
            disabled={loading && Boolean(recoveryMethod)}
          >
            Login
          </Button>
        </Form>

        {hasRecovery && (
          <>
            <div className="shim" />

            <Space
              direction="vertical"
              size={6}
              style={{
                width: "100%",
              }}
            >
              {recover_email && (
                <Button
                  block
                  size={size}
                  icon={<MailOutlined />}
                  loading={
                    loading && recoveryMethod === "email"
                  }
                  disabled={
                     !watchedEmail?.trim() || (loading && recoveryMethod !== "email")
                  }
                  onClick={() => sendLoginCode("email")}
                >
                  Log in with email code
                </Button>
              )}

              {false && recover_text && (
                <Button
                  block
                  size={size}
                  icon={<MessageOutlined />}
                  loading={
                    loading && recoveryMethod === "text"
                  }
                  disabled={
                    loading && recoveryMethod !== "text"
                  }
                  onClick={() => sendLoginCode("text")}
                >
                  Log in with text code
                </Button>
              )}
            </Space>
          </>
        )}

        {show_forgot && (
          <Button
            className="inline-button pull-right"
            disabled={loading}
            onClick={() => {
              history.push("/account/forgot");

              setTimeout(() => {
                if (dismiss) {
                  dismiss();
                }
              }, 100);
            }}
          >
            I forgot my password
          </Button>
        )}

        {show_create && (
          <>
            <div aria-hidden>
              <Divider
                plain
                style={{
                  margin: "10px 0",
                }}
              >
                or
              </Divider>
            </div>

            <Link
              to="/account/signup"
              onClick={(event) => {
                if (loading) {
                  event.preventDefault();
                  return;
                }

                setTimeout(() => {
                  if (dismiss) {
                    dismiss();
                  }
                }, 100);
              }}
            >
              <div
                className="ant-btn ant-btn-default"
                style={{
                  width: "100%",
                }}
              >
                Create an account
              </div>
            </Link>
          </>
        )}

        {footer}
      </>
    );
  };

  const drawCode = () => {
    const isText = recoveryMethod === "text";

    return (
      <>
        <strong>Enter your login code</strong>

        <Divider
          dashed
          style={{
            margin: "15px 0",
          }}
        />

        <p
          style={{
            marginBottom: 16,
          }}
        >
          Enter the six-digit code sent{" "}
          {isText ? "by text to " : "to "}
          <strong>{recoveryDestination}</strong>.
        </p>

        {drawMessage()}

        <CodeInput
          resetKey={codeResetKey}
          disabled={loading}
          onChange={clearMessage}
          onComplete={verifyRecoveryCode}
        />

        {loading && (
          <p
            aria-live="polite"
            style={{
              marginTop: 12,
              textAlign: "center",
            }}
          >
            Verifying code...
          </p>
        )}

        <Space
          direction="vertical"
          size={6}
          style={{
            width: "100%",
            marginTop: 14,
          }}
        >

          {(focus_password &&
            <Button
              block
              size={size}
              disabled={loading}
              onClick={back}
            >
              {back_label}
            </Button>

          )}
          {(!focus_password &&
            <Button
              block
              size={size}
              disabled={loading}
              onClick={returnToLogin}
            >
              {back_label}
            </Button>
          )}

          <Button
            style={{ flex: 1 }}
            size={size}
            disabled={resendSeconds}
            loading={loading && Boolean(recoveryMethod)}
            onClick={() => sendLoginCode(recoveryMethod || "email", true)}
          >
            {(!resendSeconds) ? "Resend code" : `Resend code (${resendSeconds}s)`} 
          </Button>
        </Space>
      </>
    );
  };

  return (
    <div>
      {view === "login" && drawLogin()}
      {view === "code" && drawCode()}
    </div>
  );
}

export default AccountLoginForm;